import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar } from 'lucide-react';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { newsArticles, formatDate, NewsArticle, ContentSection } from '../data/news';
import { PartnerCTA } from '../components/PartnerCTA';
function renderContentSection(section: ContentSection, index: number) {
  switch (section.type) {
    case 'paragraph':
      return <p key={index} className="text-steel-600 leading-relaxed mb-6" data-id="element-1107">
          {section.content}
        </p>;
    case 'heading':
      return <h2 key={index} className="text-xl font-bold text-navy-900 mt-10 mb-4" data-id="element-1108">
          {section.content}
        </h2>;
    case 'list':
      return <ul key={index} className="mb-6 space-y-2" data-id="element-1109">
          {section.items?.map((item, i) => <li key={i} className="flex items-start gap-3 text-steel-600" data-id="element-1110">
              <span className="w-1.5 h-1.5 bg-cg-orange-600 rounded-full mt-2 flex-shrink-0" data-id="element-1111" />
              <span data-id="element-1112">{item}</span>
            </li>)}
        </ul>;
    case 'quote':
      return <blockquote key={index} className="border-l-4 border-cg-orange-600 pl-6 py-2 my-8 bg-slate-50" data-id="element-1113">
          <p className="text-lg text-navy-900 italic leading-relaxed" data-id="element-1114">
            "{section.content}"
          </p>
        </blockquote>;
    case 'image':
      return <figure key={index} className="my-8" data-id="element-1115">
          <img src={section.src} alt={section.alt || ''} className="w-full rounded-sm" data-id="element-1116" />
          {section.caption && <figcaption className="text-sm text-steel-500 mt-2 text-center" data-id="element-1117">
              {section.caption}
            </figcaption>}
        </figure>;
    default:
      return null;
  }
}
export function NewsArticlePage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const article = newsArticles.find(a => a.slug === slug);
  if (!article) {
    return <Navigate to="/about/news" replace data-id="element-1118" />;
  }
  // Get related articles (same category, excluding current, max 3)
  const relatedArticles = newsArticles.filter(a => a.slug !== slug).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);
  return <>
      {/* Hero */}
      <section className="bg-navy-900 pt-40 pb-16" data-id="element-1119">
        <div className="max-w-4xl mx-auto px-6 lg:px-8" data-id="element-1120">
          <div className="border-l-4 border-cg-orange-600 pl-6" data-id="element-1121">
            <span className="text-sm font-bold uppercase tracking-[0.2em] text-cg-orange-600 block mb-3" data-id="element-1122">
              {article.category}
            </span>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight" data-id="element-1123">
              {article.title}
            </h1>
            <div className="flex items-center gap-2 text-steel-400 text-sm" data-id="element-1124">
              <Calendar className="w-4 h-4" data-id="element-1125" />
              <span data-id="element-1126">{formatDate(article.date)}</span>
            </div>
          </div>
        </div>
      </section>

      <Breadcrumbs items={[{
      label: 'About Us',
      to: '/about'
    }, {
      label: 'News and Insights',
      to: '/about/news'
    }, {
      label: article.title
    }]} data-id="element-1127" />

      {/* Hero Image */}
      <section className="bg-white" data-id="element-1128">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 -mt-8" data-id="element-1129">
          <div className="aspect-[16/9] overflow-hidden rounded-sm shadow-lg" data-id="element-1130">
            <img src={article.heroImage} alt={article.title} className="w-full h-full object-cover" data-id="element-1131" />
          </div>
        </div>
      </section>

      {/* Article Body */}
      <article className="bg-white py-16" data-id="element-1132">
        <div className="max-w-3xl mx-auto px-6 lg:px-8" data-id="element-1133">
          <div className="prose-lg" data-id="element-1134">
            {article.body.map((section, index) => renderContentSection(section, index))}
          </div>
        </div>
      </article>

      {/* End of Article CTAs */}
      <section className="bg-slate-50 py-16 border-t border-steel-200" data-id="element-1135">
        <div className="max-w-3xl mx-auto px-6 lg:px-8" data-id="element-1136">
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1137">
            <Link to="/about/news" className="inline-flex items-center justify-center px-8 py-4 border border-navy-900 text-navy-900 hover:bg-navy-900 hover:text-white font-bold uppercase tracking-wider text-sm rounded-sm transition-colors gap-2" data-id="element-1138">
              <ArrowLeft className="w-4 h-4" data-id="element-1139" /> Back to News and Insights
            </Link>
          </div>
        </div>
      </section>

      {/* Related Articles */}
      {relatedArticles.length > 0 && <section className="bg-white py-20 border-t border-steel-200" data-id="element-1140">
          <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1141">
            <h2 className="text-2xl font-bold text-navy-900 mb-8" data-id="element-1142">
              Latest articles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1143">
              {relatedArticles.map(related => <Link key={related.slug} to={`/about/news/${related.slug}`} className="bg-white border border-steel-200 rounded-sm overflow-hidden hover:border-cg-orange-600 transition-colors group flex flex-col" data-id="element-1144">
                  <div className="aspect-[16/10] overflow-hidden bg-slate-100" data-id="element-1145">
                    <img src={related.heroImage} alt={related.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-1146" />
                  </div>
                  <div className="p-5 flex flex-col flex-1" data-id="element-1147">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-cg-orange-600 mb-2" data-id="element-1148">
                      {related.category}
                    </span>
                    <h3 className="text-sm font-bold text-navy-900 mb-2 group-hover:text-cg-orange-600 transition-colors line-clamp-2" data-id="element-1149">
                      {related.title}
                    </h3>
                    <span className="text-[10px] text-steel-400" data-id="element-1150">
                      {formatDate(related.date)}
                    </span>
                  </div>
                </Link>)}
            </div>
          </div>
        </section>}

      <PartnerCTA data-id="element-1151" />
    </>;
}